import { Alert, Box, Button, Link, Stack, Typography, useMediaQuery } from '@mui/material'
import { DownloadOutlined } from '@mui/icons-material'
import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import gaiaTheme from '../../../../root-components/theme'
import { IHeadCell } from '../../../../shared/interfaces/table-display.model'
import { TableDisplay } from '../../../presentation-components/miscellaneous-components/table-display'
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service'
import { IReportPeriod } from '../../../../shared/services/report-period/report-period.model'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { DownloadStatusContext } from '../../../../context/download-status/download-status'
import FileDownloadService from '../../../../shared/services/file-download/file-download.service'
import ManagementSystemCertificationService from '../../../../shared/services/management-system-certification/management-system-certification.service'
import { IManagementSystemCertification } from '../../../../shared/services/management-system-certification/management-system-certification.model'
import { LoadingPage } from '../../loading-page'
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container'
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading'
import { ISO14001_SHEETS, ISO14001_STANDING_STATEMENT, ISO14001_WORKBOOK_TYPE } from '../../../../shared/constants/iso14001-workbook'

const CERTIFICATIONS_TAB = '/reporting/regulations?tab=certifications'
const formatLongDate = (isoDate: string): string => moment(isoDate, 'YYYY-MM-DD').format('D MMMM YYYY')

// The audit pack page's shape: one row per reporting period with a Download button, the app-wide
// download tracker polling the file and offering it when ready. The workbook certifies nothing and
// the page says so above the table.
export const ReportingIso14001Page = () => {
  const navigate = useNavigate()
  const reportPeriodService = new ReportPeriodService()
  const fileDownloadService = new FileDownloadService()
  const certificationService = new ManagementSystemCertificationService()
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const userNotificationsContext = useContext(UserNotificationsContext)
  const downloadStatusContext = useContext(DownloadStatusContext)
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'))

  const [reportPeriods, setReportPeriods] = useState<IReportPeriod[]>([])
  const [certificates, setCertificates] = useState<IManagementSystemCertification[] | null>(null)
  const [selected, setSelected] = useState<string[]>([])
  const [perPage, setPerPage] = useState(100)
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const companyName = (!userDetails?.isPartner && userDetails?.partnerOrganisation) || (userDetails?.isPartner && userDetails?.isLoggedInAsClient)
    ? userDetails?.partnerOrganisation?.partnerName
    : 'Gaia'

  useEffect(() => {
    reportPeriodService.getAllReportPeriod()
      .then((response) => {
        const sorted = [...response].sort((a, b) => Number(b.calendarYear) - Number(a.calendarYear))
        setReportPeriods(sorted)
        setIsLoading(false)
      })
      .catch((err) => {
        console.log(err)
        setReportPeriods([])
        setIsLoading(false)
      })
    // The certificate prompts load in the background and never gate the table.
    const organisationId = userDetails?.organisation?.id
    if (organisationId) {
      certificationService.getAll({ holderModelName: 'organisation', holderObjectId: organisationId })
        .then((response) => setCertificates(response))
        .catch((err) => { console.log(err); setCertificates(null) })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // One in-flight workbook per period: without the guard every extra click queued another
  // identical export behind the first.
  const [queuedPeriodIds, setQueuedPeriodIds] = useState<string[]>([])

  const downloadHandler = (reportPeriodId: string) => {
    if (!userDetails?.organisation.subscriptionActive || (userDetails.organisation.subscriptionActive && userDetails.organisation.freeTrialDaysRemaining && userDetails.organisation.freeTrialDaysRemaining > 0)) {
      userNotificationsContext.openDialog({ active: true, hideCancelButton: true, title: 'Access Denied', message: `You must have a subscription to ${companyName} Carbon Accounting to be able to generate the ISO 14001 evidence workbook.`, buttonCopy: 'Ok' })
      return
    }
    if (queuedPeriodIds.includes(reportPeriodId)) return
    setQueuedPeriodIds(ids => [...ids, reportPeriodId])
    fileDownloadService.createFileDownloadEntry(ISO14001_WORKBOOK_TYPE, reportPeriodId, null)
      .then((response) => downloadStatusContext.startDownloadTracking(response.id))
      .catch((err) => {
        console.log(err)
        userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' })
      })
      .finally(() => setQueuedPeriodIds(ids => ids.filter(id => id !== reportPeriodId)))
  }

  const headCells: IHeadCell[] = [
    { id: 'reportPeriod', numeric: false, label: 'Report Period', sortable: false, isHeading: true },
    { id: 'action', numeric: false, align: 'right', label: 'Evidence workbook', sortable: false },
  ]

  const rows = reportPeriods.map((reportPeriod) => ({
    id: reportPeriod.id,
    reportPeriod: reportPeriod.friendlyName || reportPeriod.calendarYear,
    action: (
      <Button
        variant='outlined'
        startIcon={<DownloadOutlined />}
        onClick={() => downloadHandler(reportPeriod.id)}
        disabled={queuedPeriodIds.includes(reportPeriod.id)}
        sx={{ textTransform: 'none' }}
      >
        {queuedPeriodIds.includes(reportPeriod.id) ? 'Preparing…' : 'Download'}
      </Button>
    ),
  }))

  const transitionCertificate = certificates?.find((c) => c.derived.needsTransition && c.derived.status !== 'expired') || null
  const certificationsLink = (
    <Link component='button' type='button' onClick={() => navigate(CERTIFICATIONS_TAB)} sx={{ verticalAlign: 'baseline' }}>
      Regulations, Certifications
    </Link>
  )

  return (
    <PageOuterContainer>
      <PageHeading
        title='ISO 14001 evidence'
        includeBackButton={true}
        backButtonLink='/reporting'
        backButtonText='Back to reporting'
        hasTabs={false}
      />
      <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2, maxWidth: 760 }}>
        {ISO14001_STANDING_STATEMENT} Generated files also appear in Settings, Downloaded Files.
      </Typography>
      {transitionCertificate && transitionCertificate.derived.effectiveExpiresOn && (
        <Alert severity='warning' sx={{ mb: 2, maxWidth: 760 }}>
          Your {transitionCertificate.derived.label} certificate ceases to be valid on {formatLongDate(transitionCertificate.derived.effectiveExpiresOn)}, at the end of the
          transition to the {transitionCertificate.derived.supersededBy} edition (indicative date: confirm it with your certification body). Review it under {certificationsLink}.
        </Alert>
      )}
      {certificates && certificates.length === 0 && (
        <Typography variant='body2' sx={{ color: 'text.secondary', mb: 2 }}>
          Record your certificate under {certificationsLink} to include it on the cover sheet.
        </Typography>
      )}
      <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', p: 3, mb: 3 }}>
        <Typography variant='subtitle1' sx={{ color: 'text.primary', mb: 1 }}>What the workbook contains</Typography>
        <Stack spacing={0.75}>
          {ISO14001_SHEETS.map((sheet) => (
            <Typography key={sheet.key} variant='body2' sx={{ color: 'text.secondary' }}>
              <Box component='span' sx={{ color: 'text.primary' }}>{sheet.title}.</Box> {sheet.description}
            </Typography>
          ))}
        </Stack>
      </Box>
      <LoadingPage pageReady={!isLoading}>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          isMobileMode={isMobileMode}
          selected={selected}
          setSelected={setSelected}
          totalRecords={rows.length}
          perPage={perPage}
          setPerPage={setPerPage}
        />
      </LoadingPage>
    </PageOuterContainer>
  )
}
